import { useState } from "react";
import { ArrowUpRight, BadgeCheck } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";

export const PRICES = [
  { service: "Pre Delivery Inspection (PDI)", price: "₹1,899", luxury: "₹2,999", slug: "pdi" },
  { service: "Used Car Inspection", price: "₹1,899", luxury: "₹2,999", slug: "used-car" },
  { service: "Accident Damage Inspection", price: "₹1,499", slug: "accident" },
  { service: "Flood Damage Inspection", price: "₹1,499", slug: "flood" },
  { service: "Engine & Transmission", price: "₹1,499", slug: "engine" },
  { service: "OBD Computer Diagnostics", price: "₹999", slug: "obd" },
];

export const Pricing = ({ onBook }) => {
  const [isLuxury, setIsLuxury] = useState(false);

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="border-t border-white/10 bg-[#0a0a0a] py-28 md:py-36"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <Chapter index="02" label="Pricing" />
        </Reveal>
        <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
          <Reveal delay={0.08}>
            <h2 className="max-w-2xl font-heading text-4xl font-extrabold tracking-tight text-white md:text-6xl">
              Honest prices.
              <span className="block text-white/40">No surprises later.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-white/50 md:text-base">
              PDI &amp; used car inspections: ₹1,899 for normal cars, ₹2,999 for
              luxury brands. Final quote confirmed on call.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 flex items-center gap-4">
            <span
              className={`text-sm font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                !isLuxury ? "text-gold" : "text-white/40"
              }`}
            >
              Normal
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={isLuxury}
              data-testid="pricing-luxury-toggle"
              onClick={() => setIsLuxury((v) => !v)}
              className="relative h-8 w-16 shrink-0 rounded-full border border-gold/40 bg-white/[0.06] transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
            >
              <span
                className={`absolute top-1 h-6 w-6 rounded-full bg-gold shadow-[0_0_12px_rgba(212,175,55,0.6)] transition-transform duration-300 ease-out ${
                  isLuxury ? "translate-x-[34px]" : "translate-x-1"
                }`}
              />
            </button>
            <span
              className={`text-sm font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                isLuxury ? "text-gold" : "text-white/40"
              }`}
            >
              Luxury Brands
            </span>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PRICES.map((p, i) => {
            const activePrice = isLuxury && p.luxury ? p.luxury : p.price;
            return (
              <Reveal key={p.slug} delay={0.06 * i}>
                <article
                  data-testid={`pricing-card-${p.slug}`}
                  className="group flex h-full flex-col rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition-[transform,border-color,background-color] duration-500 hover:-translate-y-2 hover:border-gold/40 hover:bg-white/[0.05]"
                >
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">
                    {p.service}
                  </span>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span
                      data-testid={`pricing-price-${p.slug}`}
                      className="font-heading text-5xl font-extrabold tracking-tighter text-gold transition-all duration-300"
                    >
                      {activePrice}
                    </span>
                    <span className="text-xs font-semibold text-white/40">
                      / inspection
                    </span>
                  </div>
                  {p.luxury ? (
                    <span className="mt-3 inline-flex w-fit items-center rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[11px] font-bold text-gold">
                      {isLuxury ? "Luxury brand rate applied" : `Luxury brands: ${p.luxury}`}
                    </span>
                  ) : (
                    <span className="mt-3 inline-flex w-fit items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-semibold text-white/40">
                      Same for all car types
                    </span>
                  )}
                  <ul className="mt-6 flex-1 space-y-2.5">
                    {["On-site at your location", "Detailed PDF report", "Same-day slots"].map(
                      (f) => (
                        <li key={f} className="flex items-center gap-2.5 text-sm text-white/60">
                          <BadgeCheck className="h-4 w-4 shrink-0 text-gold" />
                          {f}
                        </li>
                      )
                    )}
                  </ul>
                  <button
                    data-testid={`pricing-book-${p.slug}`}
                    onClick={() => onBook(p.service)}
                    className="mt-8 flex items-center justify-center gap-2 rounded-full border border-gold/40 py-3 font-heading text-sm font-extrabold text-gold transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-black"
                  >
                    Book at {activePrice}
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
